import React from "react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, PieChart, Pie, Cell } from "recharts";
import { Users, Mars, Venus, Scale } from "lucide-react";
import { Section, Card, Kpi, GenderLegend, Empty, C, axis, tip } from "./parts";
import { ratioText } from "@/lib/workforceStats";

export const WorkforceSection = ({ w }) => {
    const { counts: c, byYear, missingDoj } = w;
    const set = c.male + c.female;
    const pct = (n) => (set ? `${Math.round((n * 100) / set)}%` : "—");
    const pie = [
        { name: "Male", value: c.male, fill: C.male },
        { name: "Female", value: c.female, fill: C.female },
        { name: "Not set", value: c.unset, fill: C.unset },
    ].filter((x) => x.value);
    return (
        <Section no="01" title="Workforce Overview" testid="wf-overview">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <Kpi label="Total employee records" value={c.total} icon={<Users className="h-3 w-3" />} sub={c.unset ? `${c.unset} without gender set` : "All have gender set"} testid="wf-total" />
                <Kpi label="Male headcount" value={c.male} tone="text-sky-700" icon={<Mars className="h-3 w-3 text-sky-600" />} sub={`${pct(c.male)} of known gender`} testid="wf-male" />
                <Kpi label="Female headcount" value={c.female} tone="text-pink-700" icon={<Venus className="h-3 w-3 text-pink-600" />} sub={`${pct(c.female)} of known gender`} testid="wf-female" />
                <Kpi label="Male-to-female ratio" value={ratioText(c.male, c.female)} icon={<Scale className="h-3 w-3" />} sub={set ? `${c.male} ${c.male === 1 ? "man" : "men"} · ${c.female} ${c.female === 1 ? "woman" : "women"}` : "Set gender to see the ratio"} testid="wf-ratio" />
            </div>
            <div className="grid lg:grid-cols-3 gap-3">
                <Card title="Gender-wise workforce %" testid="wf-gender-pie">
                    {pie.length ? (
                        <div className="h-48 relative">
                            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                                <PieChart>
                                    <Pie data={pie} dataKey="value" nameKey="name" innerRadius={50} outerRadius={75} paddingAngle={pie.length > 1 ? 2 : 0} stroke="none">
                                        {pie.map((x) => <Cell key={x.name} fill={x.fill} />)}
                                    </Pie>
                                    <Tooltip {...tip} formatter={(v, n) => [`${v} (${Math.round((v * 100) / c.total)}%)`, n]} />
                                </PieChart>
                            </ResponsiveContainer>
                            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                                <div className="text-2xl font-bold text-slate-900">{c.total}</div>
                                <div className="text-[10px] text-slate-500 uppercase tracking-wider">people</div>
                            </div>
                        </div>
                    ) : <Empty />}
                    <div className="flex justify-center mt-1"><GenderLegend /></div>
                </Card>
                <Card title="Employees by joining year" hint={missingDoj ? `${missingDoj} without joining date` : null} className="lg:col-span-2" testid="wf-by-year">
                    {byYear.length ? (
                        <div className="h-56">
                            <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 180 }}>
                                <BarChart maxBarSize={48} data={byYear} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                                    <XAxis dataKey="year" {...axis} />
                                    <YAxis allowDecimals={false} {...axis} />
                                    <Tooltip {...tip} />
                                    <Bar dataKey="male" name="Male" stackId="g" fill={C.male} />
                                    <Bar dataKey="female" name="Female" stackId="g" fill={C.female} />
                                    <Bar dataKey="unset" name="Not set" stackId="g" fill={C.unset} radius={[3, 3, 0, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    ) : <Empty>No joining dates recorded yet.</Empty>}
                    <GenderLegend />
                </Card>
            </div>
        </Section>
    );
};
